import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { DebouncedTextField } from './DebouncedTextField';
import { useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  CcfTypography,
  CcfBotIcon,
  CcfOverallSentimentSadIcon,
  CcfOverallSentimentMehIcon,
  CcfOverallSentimentSmileIcon,
} from '@nice-ccf/ui-controls';
import {
  ConversationMessage,
  IntentAndAssistance,
  IntentTask,
  SuggestionArticle,
  TaskButton,
} from './types';
import PersonIcon from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

type ConversationMedia = 'voice' | 'digital';

interface Props {
  messages: ConversationMessage[];
  onChange: (messages: ConversationMessage[]) => void;
  /** Drives which "Add Card" options are offered and the recordType they map to. */
  media: ConversationMedia;
}

/**
 * Catalog of the cards a user can add, derived from the default Voice/Chat
 * JSON templates. `create()` returns a blank message whose shape matches those
 * templates so it serialises back into the correct conversation array.
 */
interface CardPreset {
  key: string;
  label: string;
  media: ConversationMedia[];
  create: () => ConversationMessage;
}

const CARD_PRESETS: CardPreset[] = [
  {
    key: 'agent',
    label: 'Agent',
    media: ['digital'],
    create: () => ({ messageFrom: 'agent', recordType: 'nbr', message: [''] }),
  },
  {
    key: 'customer',
    label: 'Customer',
    media: ['digital'],
    create: () => ({
      messageFrom: 'customer',
      recordType: 'customerReply',
      message: [''],
    }),
  },
  {
    key: 'copilotVoice',
    label: 'Copilot',
    media: ['voice'],
    create: () => ({ messageFrom: 'copilot', recordType: 'nbr', message: [''] }),
  },
  {
    key: 'copilotDigital',
    label: 'Copilot',
    media: ['digital'],
    create: () => ({
      messageFrom: 'copilot',
      recordType: 'behavioralGuidance',
      message: [''],
    }),
  },
  {
    key: 'sentiment',
    label: 'Copilot — Sentiment',
    media: ['voice', 'digital'],
    create: () => ({
      messageFrom: 'copilot',
      recordType: 'sentiment',
      message: [{ description: '' }],
      sentimentScore: 0,
    }),
  },
  {
    key: 'kb',
    label: 'Copilot — KB Answer',
    media: ['voice', 'digital'],
    create: () => ({
      messageFrom: 'copilot',
      recordType: 'suggestionArticle',
      message: [
        {
          kbAnswers: { title: '', description: '' },
          kbInternalUse: {},
          kbLinks: [{ text: '', link: '' }],
          kbImages: [{ name: '', imageUrl: '' }],
          kbProcessSteps: [],
        },
      ],
    }),
  },
  {
    key: 'taskAssist',
    label: 'Copilot — Task Assist',
    media: ['digital'],
    create: () => ({
      messageFrom: 'copilot',
      recordType: 'intentAndAssistance',
      message: [{ intent: '', title: '', tasks: [] }],
    }),
  },
];

/** Short, friendly label for a card's record type (shown on each card). */
const recordTypeLabel: Record<string, string> = {
  nbr: 'Message',
  customerReply: 'Customer Reply',
  behavioralGuidance: 'Guidance',
  sentiment: 'Sentiment',
  suggestionArticle: 'KB Answer',
  intentAndAssistance: 'Task Assist',
};

const senderBorderColor: Record<string, string> = {
  agent: '#90caf9',
  customer: '#a5d6a7',
  copilot: '#ce93d8',
  elevatedEmail: '#ffb74d',
  elevatedSMS: '#4db6ac',
};

const senderBg: Record<string, string> = {
  agent: '#f4f9ff',
  customer: '#f4fbf5',
  copilot: '#fbf6fd',
  elevatedEmail: '#fff9f2',
  elevatedSMS: '#f2faf9',
};

/**@example */
function StringMessageEditor({
  values,
  onChange,
}: {
  values: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {values.map((v, i) => (
        <DebouncedTextField
          key={i}
          size="small"
          fullWidth
          multiline
          minRows={1}
          value={v}
          onChange={(val) =>
            onChange(values.map((x, idx) => (idx === i ? val : x)))
          }
        />
      ))}
    </Box>
  );
}

const SENTIMENT_OPTIONS = [
  {
    score: 0,
    Icon: CcfOverallSentimentSadIcon,
    label: 'Negative',
    bg: '#ffebee',
    border: '#ef9a9a',
  },
  {
    score: 1,
    Icon: CcfOverallSentimentMehIcon,
    label: 'Neutral',
    bg: '#fff9c4',
    border: '#f9a825',
  },
  {
    score: 2,
    Icon: CcfOverallSentimentSmileIcon,
    label: 'Positive',
    bg: '#e8f5e9',
    border: '#66bb6a',
  },
];

/**@example */
function SentimentEditor({
  message,
  sentimentScore,
  onMessageChange,
  onScoreChange,
}: {
  message: Array<{ description: string }>;
  sentimentScore: number | undefined;
  onMessageChange: (m: Array<{ description: string }>) => void;
  onScoreChange: (s: number) => void;
}) {
  const desc = message[0]?.description ?? '';
  const current = SENTIMENT_OPTIONS.find(
    (o) => o.score === (sentimentScore ?? 0)
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <DebouncedTextField
        size="small"
        fullWidth
        multiline
        minRows={1}
        label="Description"
        value={desc}
        onChange={(v) => onMessageChange([{ description: v }])}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <CcfTypography
          variant="caption"
          sx={{ color: '#555', fontWeight: 600 }}
        >
          Sentiment:
        </CcfTypography>
        {SENTIMENT_OPTIONS.map(({ score, Icon, label, bg, border }) => {
          const selected = (sentimentScore ?? 0) === score;
          return (
            <Box
              key={score}
              onClick={() => onScoreChange(score)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 2,
                py: 0.75,
                borderRadius: '999px',
                border: `1.5px solid ${selected ? border : '#e4e7eb'}`,
                backgroundColor: selected ? bg : '#fff',
                boxShadow: selected ? `0 0 0 1px ${bg}` : 'none',
                cursor: 'pointer',
                userSelect: 'none',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: bg,
                  borderColor: border,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Icon sx={{ fontSize: 20 }} />
              <CcfTypography
                variant="caption"
                sx={{ fontWeight: selected ? 700 : 500, color: '#444' }}
              >
                {label}
              </CcfTypography>
            </Box>
          );
        })}
        {current && (
          <CcfTypography variant="caption" sx={{ ml: 'auto', color: '#888' }}>
            Score: {current.score}
          </CcfTypography>
        )}
      </Box>
    </Box>
  );
}

/**@example */
function SuggestionArticleEditor({
  articles,
  onChange,
}: {
  articles: SuggestionArticle[];
  onChange: (a: SuggestionArticle[]) => void;
}) {
  const article: SuggestionArticle = articles[0] ?? {
    kbAnswers: { title: '', description: '' },
    kbInternalUse: {},
    kbLinks: [],
    kbImages: [],
  };

  /**@example */
  const update = (updated: SuggestionArticle) => onChange([updated]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <CcfTypography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
        📚 KB Answers
      </CcfTypography>
      <DebouncedTextField
        size="small"
        fullWidth
        label="Title"
        value={article.kbAnswers.title}
        onChange={(v) =>
          update({
            ...article,
            kbAnswers: { ...article.kbAnswers, title: v },
          })
        }
      />
      <DebouncedTextField
        size="small"
        fullWidth
        multiline
        minRows={3}
        label="Description"
        value={article.kbAnswers.description}
        onChange={(v) =>
          update({
            ...article,
            kbAnswers: { ...article.kbAnswers, description: v },
          })
        }
      />

      <Divider />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <CcfTypography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
          🔗 KB Links
        </CcfTypography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          sx={{ textTransform: 'none' }}
          onClick={() =>
            update({
              ...article,
              kbLinks: [...article.kbLinks, { text: '', link: '' }],
            })
          }
        >
          Add Link
        </Button>
      </Box>
      {article.kbLinks.map((lnk, li) => (
        <Box key={li} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <DebouncedTextField
            size="small"
            label="Text"
            value={lnk.text}
            sx={{ flex: 0.4 }}
            onChange={(v) => {
              const updated = article.kbLinks.map((l, i) =>
                i === li ? { ...l, text: v } : l
              );
              update({ ...article, kbLinks: updated });
            }}
          />
          <DebouncedTextField
            size="small"
            label="URL"
            value={lnk.link}
            sx={{ flex: 1 }}
            onChange={(v) => {
              const updated = article.kbLinks.map((l, i) =>
                i === li ? { ...l, link: v } : l
              );
              update({ ...article, kbLinks: updated });
            }}
          />
          <Tooltip title="Remove link">
            <IconButton
              size="small"
              sx={{ color: '#d32f2f' }}
              onClick={() =>
                update({
                  ...article,
                  kbLinks: article.kbLinks.filter((_, i) => i !== li),
                })
              }
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ))}

      <Divider />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <CcfTypography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
          🖼️ KB Images
        </CcfTypography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          sx={{ textTransform: 'none' }}
          onClick={() =>
            update({
              ...article,
              kbImages: [...article.kbImages, { name: '', imageUrl: '' }],
            })
          }
        >
          Add Image
        </Button>
      </Box>
      {article.kbImages.map((img, ii) => (
        <Box key={ii} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <DebouncedTextField
            size="small"
            label="Name"
            value={img.name}
            sx={{ flex: 0.4 }}
            onChange={(v) => {
              const updated = article.kbImages.map((im, i) =>
                i === ii ? { ...im, name: v } : im
              );
              update({ ...article, kbImages: updated });
            }}
          />
          <DebouncedTextField
            size="small"
            label="Image URL"
            value={img.imageUrl}
            sx={{ flex: 1 }}
            onChange={(v) => {
              const updated = article.kbImages.map((im, i) =>
                i === ii ? { ...im, imageUrl: v } : im
              );
              update({ ...article, kbImages: updated });
            }}
          />
          <Tooltip title="Remove image">
            <IconButton
              size="small"
              sx={{ color: '#d32f2f' }}
              onClick={() =>
                update({
                  ...article,
                  kbImages: article.kbImages.filter((_, i) => i !== ii),
                })
              }
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Editor for the `intentAndAssistance` (Task Assist) record type. Presents the
 * flow in plain terms — a task goal, an ordered list of steps, and per-step
 * options — instead of raw JSON. The `order` field is auto-resequenced to match
 * list position so the saved JSON stays consistent without the user managing it.
 * @example
 */
function IntentAndAssistanceEditor({
  intents,
  onChange,
}: {
  intents: IntentAndAssistance[];
  onChange: (v: IntentAndAssistance[]) => void;
}) {
  const data: IntentAndAssistance = intents[0] ?? {
    intent: '',
    title: '',
    tasks: [],
  };

  /**@example */
  const update = (updated: IntentAndAssistance) => onChange([updated]);

  /**@example */
  const resequence = (tasks: IntentTask[]): IntentTask[] =>
    tasks.map((t, i) => ({ ...t, order: i }));

  /**@example */
  const setGoal = (value: string) =>
    update({ ...data, intent: value, title: value });

  /**@example */
  const updateTask = (index: number, updated: IntentTask) =>
    update({
      ...data,
      tasks: data.tasks.map((t, i) => (i === index ? updated : t)),
    });

  /**@example */
  const addTask = () =>
    update({
      ...data,
      tasks: resequence([
        ...data.tasks,
        { order: data.tasks.length, text: '' },
      ]),
    });

  /**@example */
  const removeTask = (ti: number) =>
    update({ ...data, tasks: resequence(data.tasks.filter((_, i) => i !== ti)) });

  /**@example */
  const updateButton = (ti: number, bi: number, patch: Partial<TaskButton>) => {
    const task = data.tasks[ti];
    const buttons = (task.buttons ?? []).map((b, i) =>
      i === bi ? { ...b, ...patch } : b
    );
    updateTask(ti, { ...task, buttons });
  };

  /**@example */
  const addButton = (ti: number) => {
    const task = data.tasks[ti];
    updateTask(ti, {
      ...task,
      buttons: [...(task.buttons ?? []), { text: '', action: 'NextStep' }],
    });
  };

  /**@example */
  const removeButton = (ti: number, bi: number) => {
    const task = data.tasks[ti];
    updateTask(ti, {
      ...task,
      buttons: (task.buttons ?? []).filter((_, i) => i !== bi),
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <CcfTypography
        variant="caption"
        sx={{ fontWeight: 600, color: '#00695c' }}
      >
        🎯 Task Assist — a guided flow the agent can run
      </CcfTypography>

      <DebouncedTextField
        size="small"
        fullWidth
        label="Task goal"
        helperText='Shown as the flow title (e.g. "Book tickets for a 10 day trip to Iceland")'
        value={data.title || data.intent}
        onChange={(v) => setGoal(v)}
      />

      <Divider textAlign="center">
        <CcfTypography
          variant="caption"
          sx={{ fontWeight: 600, color: '#555' }}
        >
          Steps
        </CcfTypography>
      </Divider>

      {data.tasks.map((task, ti) => {
        const buttons = task.buttons ?? [];
        return (
          <Box
            key={ti}
            sx={{
              border: '1px dashed #cfd8dc',
              borderRadius: 1,
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={`Step ${ti + 1}`}
                size="small"
                sx={{
                  backgroundColor: '#00897b',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 11,
                }}
              />
              <Tooltip title="Remove step">
                <IconButton
                  size="small"
                  sx={{ ml: 'auto', color: '#d32f2f' }}
                  onClick={() => removeTask(ti)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <DebouncedTextField
              size="small"
              fullWidth
              multiline
              minRows={1}
              label="Message"
              value={task.text}
              onChange={(v) => updateTask(ti, { ...task, text: v })}
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                pl: 1,
                borderLeft: '2px solid #b2dfdb',
              }}
            >
              {buttons.length > 0 && (
                <CcfTypography
                  variant="caption"
                  sx={{ color: '#00695c', fontWeight: 600 }}
                >
                  Options
                </CcfTypography>
              )}
              {buttons.map((btn, bi) => (
                <Box
                  key={bi}
                  sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                >
                  <DebouncedTextField
                    size="small"
                    label="Option"
                    value={btn.text}
                    fullWidth
                    onChange={(v) => updateButton(ti, bi, { text: v })}
                  />
                  <Tooltip title="Remove option">
                    <IconButton
                      size="small"
                      sx={{ color: '#d32f2f' }}
                      onClick={() => removeButton(ti, bi)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
              <Button
                size="small"
                sx={{ textTransform: 'none', alignSelf: 'flex-start', color: '#00897b' }}
                startIcon={<AddIcon />}
                onClick={() => addButton(ti)}
              >
                Add Option
              </Button>
            </Box>
          </Box>
        );
      })}

      <Button
        size="small"
        variant="outlined"
        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
        onClick={addTask}
      >
        + Add Step
      </Button>
    </Box>
  );
}

/**@example */
function MessageCardEditor({
  msg,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  msg: ConversationMessage;
  onUpdate: (updated: ConversationMessage) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  /**@example */
  const renderMessageBody = () => {
    switch (msg.recordType) {
      case 'nbr':
      case 'customerReply':
      case 'behavioralGuidance':
        return (
          <StringMessageEditor
            values={msg.message as string[]}
            onChange={(v) => onUpdate({ ...msg, message: v })}
          />
        );
      case 'sentiment':
        return (
          <SentimentEditor
            message={msg.message as Array<{ description: string }>}
            sentimentScore={msg.sentimentScore}
            onMessageChange={(m) => onUpdate({ ...msg, message: m })}
            onScoreChange={(s) => onUpdate({ ...msg, sentimentScore: s })}
          />
        );
      case 'suggestionArticle':
        return (
          <SuggestionArticleEditor
            articles={msg.message as SuggestionArticle[]}
            onChange={(a) => onUpdate({ ...msg, message: a })}
          />
        );
      case 'intentAndAssistance':
        return (
          <IntentAndAssistanceEditor
            intents={msg.message as IntentAndAssistance[]}
            onChange={(a) => onUpdate({ ...msg, message: a })}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        border: '1px solid #eef0f2',
        borderRadius: 2,
        borderLeft: `2px solid ${senderBorderColor[msg.messageFrom] ?? '#ccc'}`,
        p: 2,
        backgroundColor: senderBg[msg.messageFrom] ?? '#fafafa',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Chip
          icon={
            msg.messageFrom === 'copilot' ? (
              <CcfBotIcon sx={{ fontSize: 14 }} fillColor="#fff" />
            ) : msg.messageFrom === 'agent' ? (
              <SupportAgentIcon style={{ fontSize: 14, color: '#fff' }} />
            ) : msg.messageFrom === 'customer' ? (
              <PersonIcon style={{ fontSize: 14, color: '#fff' }} />
            ) : undefined
          }
          label={msg.messageFrom}
          size="small"
          sx={{
            backgroundColor: senderBorderColor[msg.messageFrom] ?? '#ccc',
            color: '#fff',
            fontWeight: 600,
            fontSize: 11,
            textTransform: 'capitalize',
            '& .MuiChip-icon': { color: '#fff', margin: '0px' },
            '& .MuiChip-label': { padding: '0px' },
            padding: '15px 10px',
          }}
        />
        <Chip
          label={recordTypeLabel[msg.recordType] ?? msg.recordType}
          size="small"
          variant="outlined"
          sx={{ fontSize: 10, color: '#6b7a83', borderColor: '#d6dde1' }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
          <Tooltip title="Move up">
            <span>
              <IconButton
                size="small"
                disabled={!onMoveUp}
                onClick={onMoveUp}
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down">
            <span>
              <IconButton
                size="small"
                disabled={!onMoveDown}
                onClick={onMoveDown}
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remove card">
            <IconButton
              size="small"
              onClick={onRemove}
              sx={{ color: '#d32f2f' }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {renderMessageBody()}
    </Box>
  );
}

/**@example */
export function CopilotConversationSection({
  messages,
  onChange,
  media,
}: Props) {
  const [addAnchor, setAddAnchor] = useState<null | HTMLElement>(null);

  // Stable keys — one per slot in the `messages` array (including hidden
  // elevationData entries so indices stay aligned). Maintained alongside every
  // mutation so React moves component instances with their cards instead of
  // reusing instances by position, which would leave DebouncedTextField local
  // state (and pending debounce timers) attached to the wrong card after a move.
  const keyCounter = useRef(0);
  const stableKeys = useRef<string[]>([]);

  // Sync on length changes caused by external resets (hydration, media switch).
  // Grow: append new keys for cards that appeared.
  // Shrink: drop keys for cards that were removed externally.
  // Same-length reloads keep existing keys — DebouncedTextField will sync its
  // local value via useEffect([externalValue]) since isEditing is false.
  if (stableKeys.current.length < messages.length) {
    while (stableKeys.current.length < messages.length) {
      stableKeys.current.push(`card-${keyCounter.current++}`);
    }
  } else if (stableKeys.current.length > messages.length) {
    stableKeys.current.length = messages.length;
  }

  /**@example */
  const updateMessage = (index: number, updated: ConversationMessage) =>
    onChange(messages.map((m, i) => (i === index ? updated : m)));

  /**@example */
  const removeMessage = (index: number) => {
    stableKeys.current.splice(index, 1);
    onChange(messages.filter((_, i) => i !== index));
  };

  /** Swap two visible cards by their position in the visible-only list, skipping elevationData entries. @example moveMessage(1, -1) */
  const moveMessage = (visiblePos: number, direction: -1 | 1) => {
    const visibleIndices = messages
      .map((m, i) => (m.recordType !== 'elevationData' ? i : -1))
      .filter((i) => i !== -1);
    const targetPos = visiblePos + direction;
    if (targetPos < 0 || targetPos >= visibleIndices.length) return;
    const fromIdx = visibleIndices[visiblePos];
    const toIdx = visibleIndices[targetPos];
    // Swap stable keys in lock-step with the data so the component instance
    // travels with its card and preserves local DebouncedTextField state.
    [stableKeys.current[fromIdx], stableKeys.current[toIdx]] = [
      stableKeys.current[toIdx],
      stableKeys.current[fromIdx],
    ];
    const next = [...messages];
    [next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]];
    onChange(next);
  };

  /** Append a blank card built from the chosen preset. @example addCard(preset) */
  const addCard = (preset: CardPreset) => {
    stableKeys.current.push(`card-${keyCounter.current++}`);
    onChange([...messages, preset.create()]);
    setAddAnchor(null);
  };

  const options = CARD_PRESETS.filter((p) => p.media.includes(media));

  // Pre-compute visible (non-elevationData) indices so each card knows its
  // position among visible peers — used to disable the first/last move buttons.
  const visibleIndices = messages
    .map((m, i) => (m.recordType !== 'elevationData' ? i : -1))
    .filter((i) => i !== -1);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {messages.map((msg, index) => {
        // Elevated email/SMS entries are preserved in the data but not shown
        // in the editor UI.
        if (msg.recordType === 'elevationData') return null;
        const visiblePos = visibleIndices.indexOf(index);
        return (
          <MessageCardEditor
            key={stableKeys.current[index]}
            msg={msg}
            onUpdate={(updated) => updateMessage(index, updated)}
            onRemove={() => removeMessage(index)}
            onMoveUp={visiblePos > 0 ? () => moveMessage(visiblePos, -1) : undefined}
            onMoveDown={visiblePos < visibleIndices.length - 1 ? () => moveMessage(visiblePos, 1) : undefined}
          />
        );
      })}

      {messages.length === 0 && (
        <CcfTypography
          variant="body2"
          sx={{ color: '#9e9e9e', textAlign: 'center', py: 2 }}
        >
          No messages.
        </CcfTypography>
      )}

      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={(e) => setAddAnchor(e.currentTarget)}
        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
      >
        Add Card
      </Button>
      <Menu
        anchorEl={addAnchor}
        open={Boolean(addAnchor)}
        onClose={() => setAddAnchor(null)}
      >
        {options.map((preset) => (
          <MenuItem key={preset.key} onClick={() => addCard(preset)}>
            {preset.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

export default CopilotConversationSection;
